import { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LogOut,
  Banknote,
  Wallet,
  TrendingUp,
  Receipt,
  Plus,
  Loader2,
  Filter,
  ArrowDownToLine,
} from "lucide-react";
import { toast } from "sonner";
import { Seo } from "@/lib/seo";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { generatePayments } from "@/lib/mockData";

const STATUS_STYLES = {
  Paid: "bg-emerald-50 text-emerald-700",
  Pending: "bg-amber-50 text-amber-700",
  Refunded: "bg-slate-100 text-slate-600",
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [payments, setPayments] = useState(() => generatePayments(8));
  const [filter, setFilter] = useState("All");
  const [payingOut, setPayingOut] = useState(false);
  const [invoice, setInvoice] = useState({ customer: "", amount: "" });
  const [creating, setCreating] = useState(false);

  const filtered = useMemo(
    () => (filter === "All" ? payments : payments.filter((p) => p.status === filter)),
    [payments, filter]
  );

  const totals = useMemo(() => {
    const paid = payments.filter((p) => p.status === "Paid");
    const revenue = paid.reduce((sum, p) => sum + parseFloat(p.amount), 0);
    const pending = payments.filter((p) => p.status === "Pending").length;
    return { revenue, count: paid.length, pending };
  }, [payments]);

  const createInvoice = async (e) => {
    e.preventDefault();
    if (!invoice.customer || !invoice.amount) return;
    setCreating(true);
    await new Promise((r) => setTimeout(r, 800));
    const newItem = {
      id: `INV-${Math.floor(Math.random() * 9000 + 1000)}`,
      customer: invoice.customer,
      amount: parseFloat(invoice.amount).toFixed(2),
      currency: "USD",
      method: "Invoice Link",
      status: "Pending",
      date: new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" }),
      time: new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }),
    };
    setPayments((prev) => [newItem, ...prev].slice(0, 12));
    setInvoice({ customer: "", amount: "" });
    setCreating(false);
    toast.success(`Invoice sent to ${newItem.customer}`);
  };

  const requestPayout = async () => {
    setPayingOut(true);
    await new Promise((r) => setTimeout(r, 1100));
    setPayingOut(false);
    toast.success("Instant payout requested — funds arriving shortly");
  };

  const signOut = () => {
    logout();
    toast.info("Signed out");
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-canvas">
      <Seo
        title="Business Dashboard"
        description="Elikya business owner portal."
        path="/dashboard"
        robots="noindex, nofollow"
      />

      <header className="sticky top-0 z-40 border-b border-stroke bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 sm:px-8">
          <Logo />
          <button
            onClick={signOut}
            data-testid="dashboard-signout"
            aria-label="Sign out"
            className="inline-flex items-center gap-1.5 rounded-lg border border-stroke bg-white px-3 py-2 text-xs font-semibold text-ink hover:bg-brand-tint"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-8 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-ink">
              {user ? `Welcome back, ${user.name.split(" ")[0]}` : "Business Dashboard"}
            </h1>
            <p className="mt-1 text-sm text-slatey">
              {user?.org ? user.org : "Your payments at a glance"}
            </p>
          </div>
          {!user && (
            <Link to="/login" className="text-sm font-semibold text-brand hover:text-brand-hover">
              Sign in to your account →
            </Link>
          )}
        </div>

        {/* Overview cards */}
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-2xl border border-stroke bg-white p-5 shadow-sm">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slatey">
              <Wallet className="h-4 w-4 text-brand" /> Revenue Processed
            </div>
            <p className="mt-2 text-2xl font-bold tabular-nums text-ink">
              ${totals.revenue.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            <p className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-emerald-600">
              <TrendingUp className="h-3.5 w-3.5" /> +24.6% vs last month
            </p>
          </div>
          <div className="rounded-2xl border border-stroke bg-white p-5 shadow-sm">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slatey">
              <Receipt className="h-4 w-4 text-brand" /> Payments Received
            </div>
            <p className="mt-2 text-2xl font-bold tabular-nums text-ink">{totals.count}</p>
            <p className="mt-1 text-xs text-slatey">{totals.pending} pending</p>
          </div>
          <div className="rounded-2xl border border-stroke bg-white p-5 shadow-sm">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slatey">
              <TrendingUp className="h-4 w-4 text-brand" /> Success Rate
            </div>
            <p className="mt-2 text-2xl font-bold tabular-nums text-ink">99.2%</p>
            <p className="mt-1 text-xs text-slatey">last 30 days</p>
          </div>
          {/* Payout card */}
          <div className="rounded-2xl border border-brand/20 bg-brand-tint p-5">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-brand">
              <Banknote className="h-4 w-4" /> Available Balance
            </div>
            <p className="mt-2 text-2xl font-bold tabular-nums text-ink">
              ${totals.revenue.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            <button
              onClick={requestPayout}
              disabled={payingOut}
              data-testid="request-payout"
              className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-brand py-2.5 text-sm font-semibold text-white hover:bg-brand-hover disabled:opacity-70"
            >
              {payingOut ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowDownToLine className="h-4 w-4" />}
              {payingOut ? "Requesting…" : "Request Instant Payout"}
            </button>
          </div>
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_1.7fr]">
          {/* Invoice generator */}
          <section className="rounded-2xl border border-stroke bg-white p-6 shadow-sm">
            <div className="flex items-center gap-2 text-sm font-semibold text-ink">
              <Receipt className="h-4 w-4 text-brand" /> Quick Invoice
            </div>
            <p className="mt-1 text-xs text-slatey">Create and send a payment request in seconds.</p>
            <form onSubmit={createInvoice} className="mt-4 space-y-4">
              <div>
                <label htmlFor="inv-customer" className="block text-sm font-medium text-ink">
                  Customer name
                </label>
                <input
                  id="inv-customer"
                  value={invoice.customer}
                  onChange={(e) => setInvoice((f) => ({ ...f, customer: e.target.value }))}
                  placeholder="Amara Okonkwo"
                  required
                  data-testid="invoice-customer"
                  className="brand-ring mt-1.5 w-full rounded-xl border border-stroke bg-white px-4 py-2.5 text-sm text-ink placeholder:text-slate-400 transition-shadow"
                />
              </div>
              <div>
                <label htmlFor="inv-amount" className="block text-sm font-medium text-ink">
                  Amount (USD)
                </label>
                <input
                  id="inv-amount"
                  type="number"
                  min="1"
                  step="0.01"
                  value={invoice.amount}
                  onChange={(e) => setInvoice((f) => ({ ...f, amount: e.target.value }))}
                  placeholder="42.00"
                  required
                  data-testid="invoice-amount"
                  className="brand-ring mt-1.5 w-full rounded-xl border border-stroke bg-white px-4 py-2.5 text-sm text-ink placeholder:text-slate-400 transition-shadow"
                />
              </div>
              <button
                type="submit"
                disabled={creating}
                data-testid="create-invoice"
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-brand py-2.5 text-sm font-semibold text-white hover:bg-brand-hover disabled:opacity-70"
              >
                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                {creating ? "Sending…" : "Create & Send Invoice"}
              </button>
            </form>
          </section>

          {/* Payments table */}
          <section className="rounded-2xl border border-stroke bg-white shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-stroke px-5 py-4">
              <h2 className="flex items-center gap-2 text-sm font-semibold text-ink">
                <Receipt className="h-4 w-4 text-brand" /> Recent Customer Payments
              </h2>
              <div className="flex items-center gap-1.5" data-testid="status-filter">
                <Filter className="mr-1 h-3.5 w-3.5 text-slatey" aria-hidden="true" />
                {["All", "Paid", "Pending", "Refunded"].map((s) => (
                  <button
                    key={s}
                    onClick={() => setFilter(s)}
                    data-testid={`filter-${s.toLowerCase()}`}
                    className={`rounded-lg px-2.5 py-1 text-xs font-semibold transition-colors ${
                      filter === s ? "bg-brand text-white" : "bg-canvas text-slatey hover:text-ink"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm" data-testid="payments-table">
                <thead>
                  <tr className="border-b border-stroke text-xs uppercase tracking-wider text-slatey">
                    <th className="px-5 py-3 font-semibold">Invoice</th>
                    <th className="px-5 py-3 font-semibold">Customer</th>
                    <th className="px-5 py-3 font-semibold">Method</th>
                    <th className="px-5 py-3 font-semibold">Amount</th>
                    <th className="px-5 py-3 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  <AnimatePresence initial={false}>
                    {filtered.map((p) => (
                      <motion.tr
                        key={p.id}
                        layout
                        initial={{ opacity: 0, backgroundColor: "rgba(66,136,206,0.08)" }}
                        animate={{ opacity: 1, backgroundColor: "rgba(255,255,255,0)" }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4 }}
                        className="border-b border-stroke/70 last:border-0"
                      >
                        <td className="px-5 py-3">
                          <span className="font-mono text-xs text-ink">{p.id}</span>
                          <p className="text-xs text-slatey">{p.date} · {p.time}</p>
                        </td>
                        <td className="px-5 py-3 text-ink">{p.customer}</td>
                        <td className="px-5 py-3 text-slatey">{p.method}</td>
                        <td className="px-5 py-3 font-semibold text-ink tabular-nums">
                          {p.amount} <span className="text-xs font-normal text-slatey">{p.currency}</span>
                        </td>
                        <td className="px-5 py-3">
                          <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${STATUS_STYLES[p.status]}`}>
                            {p.status}
                          </span>
                        </td>
                      </motion.tr>
                    ))}
                  </AnimatePresence>
                </tbody>
              </table>
              {filtered.length === 0 && (
                <p className="px-5 py-10 text-center text-sm text-slatey" data-testid="empty-state">
                  No {filter.toLowerCase()} payments yet.
                </p>
              )}
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
